import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDistanceToNow } from "date-fns";
import { apiClient } from "../../services/apiClient";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";

const DocumentList = ({ proposalId, onDocumentDeleted, refreshTrigger }) => {
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [updatingTypeIds, setUpdatingTypeIds] = useState(new Set());
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("uploaded_at");

  useEffect(() => {
    if (proposalId) {
      fetchDocuments();
      fetchDocumentTypes();
    }
  }, [proposalId, refreshTrigger]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.getProposalDocuments(proposalId);

      if (response.success) {
        setDocuments(response.data.documents || []);
      } else {
        throw new Error(response.message || "Failed to fetch documents");
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      setError(error.message);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      const response = await apiClient.getDocumentTypes();
      if (response.success) {
        setDocumentTypes(response.data.documentTypes || []);
      }
    } catch (error) {
      console.error("Error fetching document types:", error);
    }
  };

  const handleUpdateDocumentType = async (documentId, newTypeId) => {
    if (!newTypeId) return;

    try {
      setUpdatingTypeIds((prev) => new Set([...prev, documentId]));

      const response = await apiClient.updateDocumentType(
        documentId,
        newTypeId
      );

      if (response.success) {
        // Update the document in local state
        setDocuments((prev) =>
          prev.map((doc) => {
            if (doc.document_id === documentId) {
              const newType = documentTypes.find(
                (dt) => dt.type_id === parseInt(newTypeId)
              );
              return {
                ...doc,
                document_type_id: parseInt(newTypeId),
                type_name: newType?.type_name || doc.type_name,
              };
            }
            return doc;
          })
        );
      } else {
        throw new Error(response.message || "Failed to update document type");
      }
    } catch (error) {
      console.error("Error updating document type:", error);
      alert(`Failed to update document type: ${error.message}`);
    } finally {
      setUpdatingTypeIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  const handleDeleteDocument = async (documentId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      return;
    }

    try {
      setDeletingIds((prev) => new Set([...prev, documentId]));

      const response = await apiClient.deleteDocument(documentId);

      if (response.success) {
        setDocuments((prev) =>
          prev.filter((doc) => doc.document_id !== documentId)
        );
        onDocumentDeleted?.(documentId);
      } else {
        throw new Error(response.message || "Failed to delete document");
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      alert(`Failed to delete document: ${error.message}`);
    } finally {
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return { icon: "file-pdf", color: "text-red-500" };
      case "doc":
      case "docx":
        return { icon: "file-word", color: "text-blue-500" };
      case "xls":
      case "xlsx":
        return { icon: "file-excel", color: "text-emerald-500" };
      case "ppt":
      case "pptx":
        return { icon: "file-powerpoint", color: "text-orange-500" };
      default:
        return { icon: "file-alt", color: "text-slate-500" };
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Pending: { bg: "bg-amber-100", text: "text-amber-700", icon: "clock" },
      Processing: { bg: "bg-blue-100", text: "text-blue-700", icon: "spinner" },
      Completed: {
        bg: "bg-emerald-100",
        text: "text-emerald-700",
        icon: "check",
      },
      Failed: { bg: "bg-red-100", text: "text-red-700", icon: "times" },
    };

    const config = statusConfig[status] || statusConfig["Pending"];

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <FontAwesomeIcon
          icon={config.icon}
          className={`mr-1 ${status === "Processing" ? "animate-spin" : ""}`}
        />
        {status}
      </span>
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown size";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
  };

  const filteredAndSortedDocuments = documents
    .filter((doc) => {
      if (filter === "all") return true;
      return doc.type_name?.toLowerCase().includes(filter.toLowerCase());
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.original_file_name.localeCompare(b.original_file_name);
        case "type":
          return (a.type_name || "").localeCompare(b.type_name || "");
        case "size":
          return (b.file_size || 0) - (a.file_size || 0);
        case "uploaded_at":
        default:
          return new Date(b.uploaded_at) - new Date(a.uploaded_at);
      }
    });

  const documentTypeOptions = [
    ...new Set(documents.map((doc) => doc.type_name).filter(Boolean)),
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" text="Loading documents..." />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage message={error} onRetry={fetchDocuments} className="mb-6" />
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and Sort Controls */}
      {documents.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-3">
            {/* Filter by type */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Types</option>
              {documentTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            {/* Sort by */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="uploaded_at">Newest First</option>
              <option value="name">Name A-Z</option>
              <option value="type">Type</option>
              <option value="size">File Size</option>
            </select>
          </div>

          <div className="text-sm text-slate-600">
            {filteredAndSortedDocuments.length} of {documents.length} documents
          </div>
        </div>
      )}

      {/* Document List */}
      {filteredAndSortedDocuments.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <FontAwesomeIcon
            icon="file-alt"
            className="text-4xl text-slate-300 mb-4"
          />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            {documents.length === 0
              ? "No documents uploaded"
              : "No documents match your filter"}
          </h3>
          <p className="text-slate-600">
            {documents.length === 0
              ? "Upload documents to get started with AI-powered analysis"
              : "Try adjusting your filter criteria"}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredAndSortedDocuments.map((document) => {
            const fileIcon = getFileIcon(document.original_file_name);
            const isDeleting = deletingIds.has(document.document_id);
            const isUpdatingType = updatingTypeIds.has(document.document_id);

            return (
              <div
                key={document.document_id}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 mt-1">
                      <FontAwesomeIcon
                        icon={fileIcon.icon}
                        className={`text-xl ${fileIcon.color}`}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 truncate">
                        {document.original_file_name}
                      </h4>

                      <div className="flex items-center space-x-4 mt-1 text-sm text-slate-600">
                        {/* Document Type Dropdown */}
                        <div className="flex items-center space-x-2 mr-4">
                          <span className="text-xs text-slate-500">Type:</span>
                          <span>{document.type_name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-slate-500">
                            Change Type:{" "}
                          </span>
                          <div className="relative">
                            <select
                              value={document.document_type_id || ""}
                              onChange={(e) =>
                                handleUpdateDocumentType(
                                  document.document_id,
                                  e.target.value
                                )
                              }
                              disabled={isUpdatingType}
                              className="text-sm px-2 py-1 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
                            >
                              <option value="">Select Type...</option>
                              {documentTypes.map((type) => (
                                <option key={type.type_id} value={type.type_id}>
                                  {type.type_name}
                                </option>
                              ))}
                            </select>
                            {isUpdatingType && (
                              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                <FontAwesomeIcon
                                  icon="spinner"
                                  className="animate-spin text-primary-500 text-xs"
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        <span>{formatFileSize(document.file_size)}</span>
                        {document.page_count && (
                          <span>{document.page_count} pages</span>
                        )}
                        <span>
                          Uploaded{" "}
                          {formatDistanceToNow(new Date(document.uploaded_at), {
                            addSuffix: true,
                          })}
                        </span>
                      </div>

                      <div className="mt-2">
                        {getStatusBadge(document.processing_status)}
                      </div>

                      {document.uploaded_by_name && (
                        <div className="mt-1 text-xs text-slate-500">
                          By {document.uploaded_by_name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() =>
                        window.open(
                          `/api/documents/${document.document_id}`,
                          "_blank"
                        )
                      }
                      className="p-2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                      title="View document"
                      disabled={document.processing_status !== "Completed"}
                    >
                      <FontAwesomeIcon icon="eye" />
                    </button>

                    <button
                      onClick={() =>
                        handleDeleteDocument(
                          document.document_id,
                          document.original_file_name
                        )
                      }
                      disabled={isDeleting}
                      className="p-2 text-slate-400 hover:text-red-600 transition-colors duration-200 disabled:opacity-50"
                      title="Delete document"
                    >
                      {isDeleting ? (
                        <FontAwesomeIcon
                          icon="spinner"
                          className="animate-spin"
                        />
                      ) : (
                        <FontAwesomeIcon icon="trash" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DocumentList;
